// src/pages/EventoVendasPage.tsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { api } from "../services/api";
import Swal from "sweetalert2";

interface ItemVenda {
    id: number;
    produtoId: number;
    produtoNome: string;
    quantidade: number;
    precoUnitario: number;
    precoComDesconto: number;
    subtotal: number;
}

interface Venda {
    id: number;
    dataHora: string;
    valorTotal: number;
    statusId: number;
    itens?: ItemVenda[];
}

const EventoVendasPage: React.FC = () => {
    const { id: eventoId } = useParams<{ id: string }>();

    const [vendas, setVendas] = useState<Venda[]>([]);
    const [expanded, setExpanded] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [eventoDescricao, setEventoDescricao] = useState("");

    const formatDateTime = (date?: string | null) => {
        if (!date) return "—";

        const d = new Date(date);
        return d.toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const carregarVendas = useCallback(async () => {
        try {
            setLoading(true);

            const resVendas = await api.get(`/Vendas/evento/${eventoId}`);
            const resEvento = await api.get(`/evento/${eventoId}`);

            setVendas(resVendas.data || []);
            setEventoDescricao(resEvento.data?.descricao ?? "");
        } catch {
            Swal.fire("Erro", "Não foi possível carregar as vendas.", "error");
        } finally {
            setLoading(false);
        }
    }, [eventoId]);

    useEffect(() => {
        carregarVendas();
    }, [carregarVendas]);

    const confirmarEstorno = (vendaId: number) => {
        Swal.fire({
            title: "Estornar venda?",
            text: "Essa ação irá cancelar a venda e estornar o estoque.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Sim, estornar!",
            cancelButtonText: "Cancelar",
        }).then((res) => {
            if (res.isConfirmed) estornarVenda(vendaId);
        });
    };

    const estornarVenda = async (vendaId: number) => {
        try {
            await api.post(`/Vendas/${vendaId}/cancelar`);

            Swal.fire({
                icon: "success",
                title: "Venda estornada!",
                timer: 1600,
                showConfirmButton: false,
            });

            carregarVendas();
        } catch (error: any) {
            Swal.fire(
                "Erro",
                error?.response?.data?.message || "Falha ao estornar venda.",
                "error"
            );
        }
    };

    const toggleExpand = (id: number) => {
        setExpanded((prev) => (prev === id ? null : id));
    };

    const getStatusBadge = (statusId: number) => {
        if (statusId === 1)
            return <span className="badge bg-success px-3 py-2">Concluída</span>;

        if (statusId === 2)
            return <span className="badge bg-warning text-dark px-3 py-2">Pendente</span>;

        return <span className="badge bg-danger px-3 py-2">Cancelada</span>;
    };

    return (
        <div className="d-flex">
            <Sidebar />

            <div className="flex-grow-1 p-4" style={{ marginLeft: 220 }}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="mb-0">
                        🛒 Vendas —{" "}
                        <span className="text-primary">{eventoDescricao}</span>
                    </h2>

                    <button
                        className="btn btn-secondary"
                        onClick={() => (window.location.href = "/eventos")}
                    >
                        Voltar
                    </button>
                </div>

                {loading && <p>Carregando vendas...</p>}

                {!loading && vendas.length === 0 && (
                    <div className="alert alert-warning">
                        Nenhuma venda registrada para este evento.
                    </div>
                )}

                {!loading &&
                    vendas.map((venda) => {
                        const isOpen = expanded === venda.id;
                        const cancelada = venda.statusId === 3;

                        return (
                            <div
                                key={venda.id}
                                className="p-3 mb-3 rounded shadow-sm bg-white"
                                style={{
                                    borderLeft: cancelada ? "6px solid #dc3545" : "6px solid #0d6efd22",
                                    cursor: "pointer",
                                    transition: "0.3s",
                                }}
                                onClick={() => toggleExpand(venda.id)}
                            >
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h6 className="fw-bold mb-0">Venda #{venda.id}</h6> — {" "}
                                            {getStatusBadge(venda.statusId)} — {" "}
                                            {formatDateTime(venda.dataHora)} — {"  "}
                                            {venda.valorTotal.toLocaleString("pt-BR", {
                                                style: "currency",
                                                currency: "BRL",
                                            })}
                                        </div>


                                    </div>

                                    <div className="d-flex gap-2">
                                        <button
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleExpand(venda.id);
                                            }}
                                        >
                                            {isOpen ? "Fechar" : "Detalhes"}
                                        </button>

                                        {venda.statusId === 1 && (
                                            <button
                                                className="btn btn-success btn-sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    confirmarEstorno(venda.id);
                                                }}
                                            >
                                                Estornar
                                            </button>
                                        )}

                                        {venda.statusId === 3 && (
                                            <button className="btn btn-outline-secondary btn-sm" disabled>
                                                Estornada
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* ITENS EXPANDIDOS */}
                                {isOpen && venda.itens && (
                                    <div className="mt-3 p-3 bg-light rounded">
                                        <h6 className="fw-bold mb-2 text-secondary">Itens da venda</h6>

                                        {venda.itens.map((item) => (
                                            <div
                                                key={item.id}
                                                className="d-flex justify-content-between border-bottom py-2"
                                            >
                                                <span>
                                                    {item.produtoNome}{" "}
                                                    <small className="text-muted">
                                                        (x{item.quantidade})
                                                    </small>
                                                </span>

                                                <strong>
                                                    {item.subtotal.toLocaleString("pt-BR", {
                                                        style: "currency",
                                                        currency: "BRL",
                                                    })}
                                                </strong>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
            </div>
        </div>
    );
};

export default EventoVendasPage;
